import React, { useEffect, useMemo, useState } from "react";
import {
    Alert, Button, Col, DatePicker, Form, Input, InputNumber, Modal, Row, Select, Space, Table, Tag, Typography, message
} from "antd";
import moment from "moment";
import {
    listSettlementPayments,
    listUnallocatedAdvances,
    getAdvanceBalance,
    recordSettlementPayment,
    modifySettlementPayment,
    allocateSettlementAdvance
} from "../../actions/settlement.action";
import {
    checkToRecordSettlementPayment,
    checkToModifySettlementPayment
} from "../../components/app-header/app.header.module";
import { showConfirm } from "../../components/custom-components/custom-component";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const ISO_DATE = "YYYY-MM-DD";

const PAYMENT_METHODS = ["CASH", "BANK_TRANSFER", "UPI", "CHEQUE", "ADJUSTMENT", "OTHER"];
const DIRECTIONS = [
    { value: "INBOUND",  label: "INBOUND (received from operator)"  },
    { value: "OUTBOUND", label: "OUTBOUND (paid to operator)" }
];

// Money: ₹ + Indian grouping (e.g. ₹12,34,567.89), sign before the symbol.
const inr = (n) => {
    const num = Number(n || 0);
    return (num < 0 ? "-₹" : "₹") + Math.abs(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDate = (d) => d ? moment(d).format(ISO_DATE) : "—";
const fmtDateTime = (d) => d ? moment(d).format("YYYY-MM-DD HH:mm") : "—";

// Settlements that can receive payments
const PAYABLE_STATUSES = ["APPROVED", "PARTIALLY_SETTLED", "SETTLED"];


/* ============================================================================
 * Record Payment modal — covers both "allocated" (against a settlement) and
 * "advance" (no settlement) modes via the same form.
 *
 * Two ways to fix the operator:
 *  - Pass `operatorCode` as a fixed prop (used when opening from a settlement
 *    drilldown), OR
 *  - Pass `operators` array and let the user pick (used for the top-level
 *    Record Advance flow on the history page).
 * ============================================================================ */
export const RecordPaymentModal = (props) => {
    const {
        visible, onClose, onSuccess,
        operatorCode,             // fixed operator (drilldown flow)
        operators,                // operator list for picker (advance flow)
        settlementId,             // optional: if present, allocated; else advance
        suggestedDirection,       // optional: derived from net
        suggestedAmount           // optional: defaults the amount field (e.g. outstanding)
    } = props;
    const [form] = Form.useForm();
    const [saving, setSaving] = useState(false);
    const showOperatorPicker = !operatorCode && Array.isArray(operators) && operators.length > 0;

    useEffect(() => {
        if (visible) {
            form.resetFields();
            const init = {
                direction: suggestedDirection || "INBOUND",
                paymentMethod: "BANK_TRANSFER",
                paymentDate: moment(),
                amount: suggestedAmount != null ? Number(suggestedAmount) : null
            };
            if (showOperatorPicker) init.operatorCode = null;
            form.setFieldsValue(init);
        }
    }, [visible, suggestedDirection, suggestedAmount]);

    const onSubmit = () => {
        form.validateFields().then((values) => {
            setSaving(true);
            const opCode = operatorCode || values.operatorCode;
            const payload = {
                operatorCode: opCode,
                direction: values.direction,
                amount: values.amount,
                paymentMethod: values.paymentMethod,
                paymentReference: values.paymentReference || null,
                paymentDate: values.paymentDate.format(ISO_DATE),
                remarks: values.remarks || null
            };
            if (settlementId) payload.settlementId = settlementId;

            recordSettlementPayment(payload, (resp, msg) => {
                setSaving(false);
                message.success(msg || "Saved");
                onSuccess && onSuccess(resp);
                onClose && onClose();
            }, (errMsg) => {
                setSaving(false);
                message.error(errMsg);
            });
        }).catch(() => { /* validation errors already shown */ });
    };

    const titleText = settlementId
        ? "Record payment against this settlement"
        : "Record advance (unallocated)";

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            title={titleText}
            onOk={onSubmit}
            confirmLoading={saving}
            okText={settlementId ? "Record payment" : "Record advance"}
            destroyOnClose
        >
            <Form form={form} layout="vertical">
                {showOperatorPicker && (
                    <Form.Item
                        label="Operator"
                        name="operatorCode"
                        rules={[{ required: true, message: "Required" }]}
                    >
                        <Select
                            showSearch
                            placeholder="Select operator"
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option.children || "").toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {operators.map((op) => (
                                <Option key={op.operator_code} value={op.operator_code}>{op.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
                <Form.Item
                    label="Direction"
                    name="direction"
                    rules={[{ required: true, message: "Required" }]}
                >
                    <Select>
                        {DIRECTIONS.map((d) => <Option key={d.value} value={d.value}>{d.label}</Option>)}
                    </Select>
                </Form.Item>
                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item
                            label="Amount"
                            name="amount"
                            rules={[{ required: true, message: "Required" }, { type: "number", min: 0.01, message: "Must be > 0" }]}
                        >
                            <InputNumber style={{ width: "100%" }} step={1} precision={2} placeholder="0.00" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Mode"
                            name="paymentMethod"
                            rules={[{ required: true, message: "Required" }]}
                        >
                            <Select>
                                {PAYMENT_METHODS.map((m) => <Option key={m} value={m}>{m}</Option>)}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={12}>
                    <Col span={12}>
                        <Form.Item
                            label="Reference (UTR / cheque #)"
                            name="paymentReference"
                        >
                            <Input placeholder="Optional" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label="Date"
                            name="paymentDate"
                            rules={[{ required: true, message: "Required" }]}
                        >
                            <DatePicker style={{ width: "100%" }} format={ISO_DATE} />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item label="Remarks" name="remarks">
                    <TextArea rows={2} placeholder="Optional note" />
                </Form.Item>
            </Form>
        </Modal>
    );
};


/* ============================================================================
 * Modify Payment modal — edit fields OR void the payment.
 * ============================================================================ */
export const ModifyPaymentModal = (props) => {
    const { visible, onClose, onSuccess, payment } = props;
    const [form] = Form.useForm();
    const [mode, setMode] = useState("EDIT");  // EDIT | VOID
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (visible && payment) {
            form.resetFields();
            setMode("EDIT");
            form.setFieldsValue({
                amount: Number(payment.amount),
                paymentMethod: payment.payment_method,
                paymentReference: payment.payment_reference || "",
                paymentDate: payment.payment_date ? moment(payment.payment_date) : null,
                remarks: payment.remarks || "",
                voidReason: ""
            });
        }
    }, [visible, payment]);

    const onSubmit = () => {
        if (!payment) return;
        if (mode === "VOID") {
            form.validateFields(["voidReason"]).then((values) => {
                setSaving(true);
                modifySettlementPayment({
                    paymentId: payment.id,
                    void: true,
                    reason: values.voidReason
                }, (resp, msg) => {
                    setSaving(false);
                    message.success(msg || "Voided");
                    onSuccess && onSuccess(resp);
                    onClose && onClose();
                }, (errMsg) => {
                    setSaving(false);
                    message.error(errMsg);
                });
            }).catch(() => {});
            return;
        }

        // EDIT
        form.validateFields(["amount","paymentMethod","paymentDate"]).then((values) => {
            setSaving(true);
            const patch = {
                amount: values.amount,
                paymentMethod: values.paymentMethod,
                paymentReference: values.paymentReference || null,
                paymentDate: values.paymentDate.format(ISO_DATE),
                remarks: values.remarks || null
            };
            modifySettlementPayment({
                paymentId: payment.id,
                patch
            }, (resp, msg) => {
                setSaving(false);
                message.success(msg || "Updated");
                onSuccess && onSuccess(resp);
                onClose && onClose();
            }, (errMsg) => {
                setSaving(false);
                message.error(errMsg);
            });
        }).catch(() => {});
    };

    return (
        <Modal
            visible={visible}
            onCancel={onClose}
            title={`Modify payment ${payment ? "#" + payment.id : ""}`}
            onOk={onSubmit}
            confirmLoading={saving}
            okText={mode === "VOID" ? "Void this payment" : "Save changes"}
            okButtonProps={mode === "VOID" ? { danger: true } : {}}
            destroyOnClose
        >
            <Space direction="vertical" style={{ width: "100%" }}>
                <Tag color={mode === "VOID" ? "red" : "blue"}>{mode}</Tag>
                <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={() => setMode(mode === "VOID" ? "EDIT" : "VOID")}
                >
                    {mode === "VOID"
                        ? "Switch to edit fields instead"
                        : "Switch to void this payment instead"}
                </Button>
                <Form form={form} layout="vertical">
                    {mode === "EDIT" && (
                        <>
                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item
                                        label="Amount"
                                        name="amount"
                                        rules={[{ required: true }, { type: "number", min: 0.01 }]}
                                    >
                                        <InputNumber style={{ width: "100%" }} step={1} precision={2} />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Mode" name="paymentMethod" rules={[{ required: true }]}>
                                        <Select>
                                            {PAYMENT_METHODS.map((m) => <Option key={m} value={m}>{m}</Option>)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={12}>
                                <Col span={12}>
                                    <Form.Item label="Reference" name="paymentReference">
                                        <Input />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Date" name="paymentDate" rules={[{ required: true }]}>
                                        <DatePicker style={{ width: "100%" }} format={ISO_DATE} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item label="Remarks" name="remarks">
                                <TextArea rows={2} />
                            </Form.Item>
                            <Text type="secondary">
                                Direction is not editable. To change direction, void this payment and record a new one.
                            </Text>
                        </>
                    )}
                    {mode === "VOID" && (
                        <Form.Item
                            label="Reason for voiding"
                            name="voidReason"
                            rules={[{ required: true, message: "Reason is required" }]}
                        >
                            <TextArea rows={3} placeholder="e.g. Recorded against wrong settlement" />
                        </Form.Item>
                    )}
                </Form>
            </Space>
        </Modal>
    );
};


/* ============================================================================
 * PaymentsPanel — drop-in panel for the settlement drilldown.
 * Lists allocated payments + unallocated advances, plus Record Payment and
 * per-row Modify / Allocate actions.
 *
 * Props:
 *   settlementId, operatorCode, settlementStatus, netSettlement, outstanding
 *   onChange  -> parent should reload header to reflect amount_paid/status changes
 * ============================================================================ */
export const PaymentsPanel = ({
    settlementId, operatorCode, settlementStatus, netSettlement, outstanding, onChange
}) => {
    const [payments, setPayments] = useState([]);
    const [advances, setAdvances] = useState([]);
    const [advanceBalance, setAdvanceBalance] = useState(0);
    const [loading, setLoading] = useState(false);
    const [recordOpen, setRecordOpen] = useState(false);
    const [modifyOpen, setModifyOpen] = useState(false);
    const [activePayment, setActivePayment] = useState(null);
    const [allocatingPid, setAllocatingPid] = useState(null);

    const canRecord = checkToRecordSettlementPayment();
    const canModify = checkToModifySettlementPayment();
    const canRecordAgainstSettlement = canRecord && PAYABLE_STATUSES.indexOf(settlementStatus) !== -1;

    const reload = () => {
        if (!settlementId || !operatorCode) return;
        setLoading(true);
        Promise.all([
            new Promise((res, rej) => listSettlementPayments({ settlementId },
                (rows) => res(rows), (e) => rej(e))),
            new Promise((res, rej) => listUnallocatedAdvances({ operatorCode },
                (rows) => res(rows), (e) => rej(e))),
            new Promise((res, rej) => getAdvanceBalance({ operatorCode },
                (resp) => res(resp), (e) => rej(e)))
        ]).then(([pmts, advs, bal]) => {
            setPayments(pmts);
            setAdvances(advs);
            setAdvanceBalance(bal && bal.advance_balance != null ? Number(bal.advance_balance) : 0);
            setLoading(false);
        }).catch((errMsg) => {
            setLoading(false);
            message.error(errMsg);
        });
    };

    useEffect(() => { reload(); }, [settlementId, operatorCode]);

    // Direction defaulting: when net is negative, operator owes us → INBOUND.
    // When positive → we owe them → OUTBOUND.
    const suggestedDirection = useMemo(() => {
        const n = Number(netSettlement);
        if (n < 0) return "INBOUND";
        if (n > 0) return "OUTBOUND";
        return "INBOUND";
    }, [netSettlement]);
    const suggestedAmount = Number(outstanding) > 0 ? Number(outstanding) : null;

    const onAfterMutation = (resp) => {
        // Recompute response may include status change — bubble up so parent reloads header
        reload();
        if (onChange) onChange(resp && resp.recompute);
    };

    const onAllocate = (advanceRow) => {
        setAllocatingPid(advanceRow.id);
        allocateSettlementAdvance({
            paymentId: advanceRow.id,
            settlementId
        }, (resp, msg) => {
            setAllocatingPid(null);
            message.success(msg || "Allocated");
            onAfterMutation(resp);
        }, (errMsg) => {
            setAllocatingPid(null);
            message.error(errMsg);
        });
    };

    const paymentColumns = [
        { title: "Date", dataIndex: "payment_date", width: 110, render: fmtDate },
        {
            title: "Direction",
            dataIndex: "direction",
            width: 110,
            render: (d) => <Tag color={d === "INBOUND" ? "green" : "blue"}>{d}</Tag>
        },
        { title: "Mode", dataIndex: "payment_method", width: 130 },
        { title: "Amount", dataIndex: "amount", width: 110, render: inr },
        { title: "Reference", dataIndex: "payment_reference" },
        {
            title: "Status",
            width: 100,
            render: (_, r) => r.voided_at
                ? <Tag color="red">VOIDED</Tag>
                : <Tag color="default">active</Tag>
        },
        { title: "Remarks", dataIndex: "remarks" },
        {
            title: "Action",
            width: 110,
            render: (_, r) => {
                if (r.voided_at) return null;
                if (!canModify) return null;
                return (
                    <Button size="small" onClick={() => { setActivePayment(r); setModifyOpen(true); }}>
                        Modify
                    </Button>
                );
            }
        }
    ];

    const advanceColumns = [
        { title: "Date", dataIndex: "payment_date", width: 110, render: fmtDate },
        {
            title: "Direction",
            dataIndex: "direction",
            width: 110,
            render: (d) => <Tag color={d === "INBOUND" ? "green" : "blue"}>{d}</Tag>
        },
        { title: "Mode", dataIndex: "payment_method", width: 130 },
        { title: "Amount", dataIndex: "amount", width: 110, render: inr },
        { title: "Reference", dataIndex: "payment_reference" },
        { title: "Remarks", dataIndex: "remarks" },
        {
            title: "Action",
            width: 200,
            render: (_, r) => (
                <Space>
                    {canRecordAgainstSettlement && (
                        <Button
                            size="small"
                            type="primary"
                            loading={allocatingPid === r.id}
                            onClick={() => showConfirm(
                                () => onAllocate(r),
                                "Allocate advance",
                                `Allocate ${inr(r.amount)} to this settlement?`,
                                "Allocate"
                            )}
                        >
                            Allocate
                        </Button>
                    )}
                    {canModify && (
                        <Button
                            size="small"
                            onClick={() => { setActivePayment(r); setModifyOpen(true); }}
                        >
                            Modify
                        </Button>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div>
            <Row align="middle" justify="space-between" style={{ marginBottom: 8 }}>
                <Col>
                    <Title level={5} style={{ margin: 0 }}>Payments</Title>
                </Col>
                <Col>
                    {canRecord && (
                        <Button
                            type="primary"
                            disabled={!canRecordAgainstSettlement}
                            onClick={() => setRecordOpen(true)}
                            title={canRecordAgainstSettlement ? "" : `Cannot record payments against status ${settlementStatus}`}
                        >
                            Record Payment
                        </Button>
                    )}
                </Col>
            </Row>
            <Table
                rowKey="id"
                size="small"
                pagination={false}
                loading={loading}
                dataSource={payments}
                columns={paymentColumns}
                locale={{ emptyText: "No payments recorded yet" }}
            />

            {(advances.length > 0 || advanceBalance !== 0) && (
                <div style={{ marginTop: 16 }}>
                    <Alert
                        type="info"
                        showIcon
                        message={
                            <span>
                                Operator advance balance: <Text strong>{inr(advanceBalance)}</Text> ({advances.length} unallocated entr{advances.length === 1 ? "y" : "ies"})
                            </span>
                        }
                        style={{ marginBottom: 8 }}
                    />
                    <Table
                        rowKey="id"
                        size="small"
                        pagination={false}
                        dataSource={advances}
                        columns={advanceColumns}
                        title={() => <Text strong>Unallocated advances for this operator</Text>}
                    />
                </div>
            )}

            <RecordPaymentModal
                visible={recordOpen}
                onClose={() => setRecordOpen(false)}
                onSuccess={onAfterMutation}
                operatorCode={operatorCode}
                settlementId={settlementId}
                suggestedDirection={suggestedDirection}
                suggestedAmount={suggestedAmount}
            />
            <ModifyPaymentModal
                visible={modifyOpen}
                onClose={() => { setModifyOpen(false); setActivePayment(null); }}
                onSuccess={onAfterMutation}
                payment={activePayment}
            />
        </div>
    );
};
